<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-header pb-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
      <h5 class="mb-0">Reçus de paiement ({{ receipts.length }})</h5>
      <template v-if="canUpload">
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          class="d-none"
          @change="upload"
        />
        <button
          type="button"
          class="btn btn-primary"
          :disabled="uploading"
          @click="fileInput?.click()"
        >
          <span v-if="uploading" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
          <i v-else class="flaticon-add me-1"></i>
          Ajouter un reçu
        </button>
      </template>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25">
      <p v-if="!receipts.length" class="text-muted mb-0">Aucun reçu de paiement ajouté.</p>
      <div v-else class="receipts-grid">
        <a
          v-for="receipt in receipts"
          :key="receipt.id"
          :href="receipt.file"
          target="_blank"
          rel="noopener"
          class="receipt-item"
        >
          <img :src="receipt.file" alt="Reçu de paiement" loading="lazy" referrerpolicy="no-referrer" />
          <span class="receipt-meta">{{ formatDate(receipt.created_at) }}<br />{{ receipt.uploaded_by_name }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import moment from "moment";

interface Receipt {
  id: number;
  file: string;
  uploaded_by_name: string | null;
  created_at: string;
}

const UPLOAD_ERRORS: Record<string, string> = {
  "not paid": "Le reçu ne peut être ajouté que sur un bon payé.",
  "not exist": "Bon introuvable.",
  "upload failed": "L'envoi de l'image a échoué, merci de réessayer.",
};

export default defineComponent({
  name: "PaymentReceipts",
  props: {
    noteId: { type: Number, required: true },
    // driver of the bon, bon paid
    canUpload: { type: Boolean, default: false },
  },
  setup(props) {
    const { showToast } = useAppToast();
    const receipts = ref<Receipt[]>([]);
    const uploading = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);

    async function fetchReceipts() {
      if (!props.noteId) return;
      try {
        const res = await api.get(`/api/auth/payment_note_receipts/${props.noteId}`);
        receipts.value = res.data.receipts ?? [];
      } catch {
        receipts.value = [];
      }
    }

    async function upload(e: Event) {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;

      const data = new FormData();
      data.append("file", file);
      uploading.value = true;
      try {
        await api.post(`/api/auth/payment_note_receipts/${props.noteId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("Reçu de paiement ajouté", "success");
        fetchReceipts();
      } catch (err: any) {
        const res = err?.response?.data;
        const firstError = res?.errors ? (Object.values(res.errors)[0] as string[])[0] : null;
        showToast(firstError || UPLOAD_ERRORS[res?.message] || "Un erreur est survenue", "error");
      } finally {
        uploading.value = false;
      }
    }

    function formatDate(value: string) {
      return moment(value).format("YYYY-MM-DD HH:mm");
    }

    watch(() => props.noteId, fetchReceipts, { immediate: true });

    return { receipts, uploading, fileInput, upload, formatDate };
  },
});
</script>

<style scoped>
.receipts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}
.receipt-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: inherit;
  text-decoration: none;
}
.receipt-item img {
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border: 1px solid var(--ui-border, #e5e5e5);
  border-radius: 8px;
}
.receipt-meta {
  color: var(--ui-text-muted, #6c757d);
  font-size: 12px;
}
</style>
